import trace from '~/lib/trace';
import React, {useState, useEffect} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';

import sy from '~/styles';
import {rob as robApi} from '~/api/private';
import {
  Pressable,
  Text,
  Modal,
  PrimaryButton,
  Dialog,
  Checkbox,
} from '~/components/controls';

import CloseIcon from '~/images/md-icons/close/materialicons/24px.svg';
import PropTypes from 'prop-types';

const toggleSelectedComponentId = (selected, componentId) =>
  selected.includes(componentId)
    ? selected.filter((id) => id !== componentId)
    : [...selected, componentId];

const buildComponentRows = ({
  components,
  lessor,
  rob_components,
  damageRobcomponents,
  onChecked,
}) => {
  const rows = [];

  for (const [index, component] of [...components].entries()) {
    rows.push(
      <Component
        key={component.id}
        lessor={lessor}
        component={component}
        rob_components={rob_components}
        damage_rob_components={damageRobcomponents}
        isLast={index === components.length - 1}
        onChecked={onChecked.bind(null, component.id)}
      />,
    );
  }

  return rows;
};

const getDamageRobCodes = (components) => [
  ...new Set(
    components.flatMap((component) =>
      component.tasks.map((task) => task.rob_code),
    ),
  ),
];

const fetchDamageRobComponents = async ({license, request_id, components}) => {
  const {result} = await robApi.damage_components(
    license,
    request_id,
    getDamageRobCodes(components),
  );

  return result;
};

const TaskSubtitle = ({lessor, tasks, rob_components, ready}) => {
  const is_sla_item = tasks.length
    ? tasks.some((item) => isFinite(item.amount))
    : false;

  if (!ready) {
    return (
      <Text style={[sy.truncate, sy.small, sy['text-lightgray']]}>
        Niet toegestaan in schadeopdracht
      </Text>
    );
  }

  if (is_sla_item) {
    return (
      <Text style={[sy.truncate, sy.small, sy['text-lightgray']]}>
        SLA&nbsp;&middot;&nbsp;{lessor}
      </Text>
    );
  }

  let type;
  let operations;

  if (tasks.every((item) => !item.reference)) {
    type = 'Invoer';
    operations = [
      ...new Set(
        tasks
          .map(
            (task) =>
              rob_components[task.rob_code]?.operations.find(
                (item) => item.operation.code === task.operation,
              )?.operation.description,
          )
          .filter(Boolean),
      ),
    ];
  } else {
    const main_task = tasks.find((item) => item.is_main_task);
    type = main_task?.source ?? 'TecRMI';

    operations = tasks
      .sort((a, b) => {
        if (a.type && !b.type) {
          return -1;
        }

        if (!a.type && b.type) {
          return 1;
        }

        if (a.duration === b.duration) {
          return a.work_text.localeCompare(b.work_text);
        }

        return b.duration - a.duration;
      })
      .map((task) => task.work_text ?? 'Invoer');
  }

  const operationsLabel =
    operations?.length > 0 ? ` \u00b7 ${operations.join(', ')}` : '';

  return (
    <Text style={[sy.truncate, sy.small, sy['text-lightgray']]}>
      {type}
      {operationsLabel}
    </Text>
  );
};
TaskSubtitle.propTypes = {
  lessor: PropTypes.any,
  tasks: PropTypes.any,
  rob_components: PropTypes.any,
  ready: PropTypes.any,
};

const Component = ({
  lessor,
  component,
  rob_components,
  damage_rob_components,
  isLast,
  onChecked,
}) => {
  const {tasks} = component;

  const main_task = tasks.find((t) => t.is_main_task);

  const ready = !!damage_rob_components[main_task.rob_code];

  return (
    <View style={[sy['bg-white']]}>
      <View
        style={[
          sy['p-4'],
          sy['flex-row'],
          sy['items-center'],
          sy['gap-4'],
          ready ? null : sy['view-disabled'],
        ]}>
        <View>
          <Checkbox disabledTimeout={0} disabled={!ready} onPress={onChecked} />
        </View>
        <View style={[sy['flex-1']]}>
          <Text>
            {main_task.rob_code}&nbsp;&middot;&nbsp;
            {damage_rob_components[main_task.rob_code]?.description ??
              main_task.description}
          </Text>
          <TaskSubtitle
            lessor={lessor}
            tasks={tasks}
            rob_components={rob_components}
            ready={ready}
          />
        </View>
      </View>
      {!isLast && <Divider style={[{marginLeft: 56}, sy['bg-gray95']]} />}
    </View>
  );
};
Component.propTypes = {
  lessor: PropTypes.any,
  component: PropTypes.any,
  rob_components: PropTypes.any,
  damage_rob_components: PropTypes.any,
  isLast: PropTypes.any,
  onChecked: PropTypes.any,
};

const CreateDamageModal = ({
  request_id,
  license,
  lessor,
  components,
  rob_components,
  onDismiss,
  onCreate,
}) => {
  const [selected, setSelected] = useState([]);
  const [damageRobcomponents, setDamageRobcomponents] = useState([]);
  const [showCancelAddDialog, setShowCancelAddDialog] = useState(false);

  useEffect(() => {
    fetchDamageRobComponents({license, request_id, components})
      .then(setDamageRobcomponents)
      .catch(trace);
  }, [license, request_id, components]);

  const onToggleComponent = (componentId) => {
    setSelected((prevState) =>
      toggleSelectedComponentId(prevState, componentId),
    );
  };

  const componentRows = buildComponentRows({
    components,
    lessor,
    rob_components,
    damageRobcomponents,
    onChecked: onToggleComponent,
  });

  return (
    <>
      <Modal visible={true} onDismiss={onDismiss}>
        <View style={[{height: 64}, sy['flex-row'], sy['items-center']]}>
          <Pressable
            style={[sy['p-4']]}
            onPress={() => setShowCancelAddDialog(true)}>
            <CloseIcon fill="#4a4a49" />
          </Pressable>
          <View
            style={[sy['flex-1'], sy['items-center'], sy['justify-center']]}>
            <Text>Schadewerkopdracht</Text>
          </View>
          <View style={[{width: 56}]}></View>
        </View>
        <Divider style={sy['bg-gray88']} />
        <View style={[sy['p-4']]}>
          <Text>Selecteer de gewenste activiteiten om over te zetten.</Text>
        </View>
        <Divider style={sy['bg-gray88']} />
        <ScrollView style={[sy['bg-gray95'], sy['flex-1']]}>
          {componentRows}
        </ScrollView>
        <Divider style={sy['bg-gray88']} />
        <View style={[sy['p-4'], sy['flex-row'], sy['justify-end']]}>
          <PrimaryButton onPress={() => onCreate(selected)}>
            Aanmaken
          </PrimaryButton>
        </View>
      </Modal>
      <Dialog
        visible={showCancelAddDialog}
        title="Opdracht niet aanmaken?"
        onDismiss={() => setShowCancelAddDialog(false)}
        buttons={[
          {
            text: 'Annuleren',
            onPress: () => setShowCancelAddDialog(false),
          },
          {
            text: 'Niet aanmaken',
            onPress: onDismiss,
          },
        ]}>
        <View style={[sy['py-4']]}>
          <Text>
            Weet je zeker dat je dit venster wil sluiten zonder de
            schadeopdracht aan te maken?
          </Text>
        </View>
      </Dialog>
    </>
  );
};
CreateDamageModal.propTypes = {
  request_id: PropTypes.any,
  license: PropTypes.any,
  lessor: PropTypes.any,
  components: PropTypes.any,
  rob_components: PropTypes.any,
  onDismiss: PropTypes.any,
  onCreate: PropTypes.any,
};

export default CreateDamageModal;
